/**
 * Fit geometry for the matrix's global (fit-to-screen) view mode
 * (2026-08-25 design doc, V4-V6).
 *
 * The grid is scaled by `transform: scale(k)` until every visible cell is
 * inside the frame. `k` is deliberately unclamped on both ends:
 *
 * - No maximum. Enlargement is structurally self-limiting, because `k` is the
 *   `min` of both axes and the table's width is ~fixed at ~1992px (200px label
 *   column + 8 palier columns) regardless of how many dimensions exist. On any
 *   normal display width therefore binds; the realistic worst case is ~1.2x on
 *   a wide monitor with a short matrix, not runaway enlargement (V5).
 * - No minimum. "All cells shown, no matter how small" is honoured literally.
 *   Below k ~= 0.15 the hover tooltip that makes the mode usable stops being
 *   reliably hittable — a documented, accepted degradation, not a bug (V6).
 *
 * The pure geometry lives here rather than inline in `matrix-grid.tsx` so it is
 * unit-testable without mounting the grid — jsdom has no layout engine, so the
 * measurement path itself cannot be exercised in a component test. Same split
 * as `clipped-edges.ts`, `zoom.ts` and `keyboard-shortcuts.ts`.
 */

/** The subset of layout geometry this module needs. */
export interface FitMetrics {
  /** The grid wrapper's client width, in CSS px. */
  boxWidth: number;
  /** The grid wrapper's client height, in CSS px. */
  boxHeight: number;
  /** The table's unscaled offset width, in CSS px. */
  tableWidth: number;
  /** The table's unscaled offset height, in CSS px. */
  tableHeight: number;
}

/**
 * Per-axis slack, in CSS px. `border-collapse` plus sub-pixel rounding can
 * leave a mathematically exact fit a fraction over the box, which would
 * reintroduce the very scrollbar this mode exists to remove. Mirrors
 * `clipped-edges.ts`'s `EPSILON_PX` and its rationale.
 */
export const FIT_EPSILON_PX = 2;

/**
 * The scale factor that makes `tableWidth` x `tableHeight` fit inside
 * `boxWidth` x `boxHeight`. Returns 1 when either rectangle has no usable
 * size — the pre-layout state on mount, and any environment without a layout
 * engine — so the grid renders unscaled rather than collapsing to nothing.
 */
export function computeFitScale(metrics: FitMetrics): number {
  const { boxWidth, boxHeight, tableWidth, tableHeight } = metrics;
  if (boxWidth <= 0 || boxHeight <= 0) return 1;
  if (tableWidth <= 0 || tableHeight <= 0) return 1;
  return Math.min(
    (boxWidth - FIT_EPSILON_PX) / tableWidth,
    (boxHeight - FIT_EPSILON_PX) / tableHeight,
  );
}
